import { useState } from "react";
import { ArrowLeft, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";

const CATEGORIES = [
  { id: "stationery", label: "Stationery" },
  { id: "scrapbook_junk_journal", label: "Scrapbook & Junk Journal" },
  { id: "coloring_pages", label: "Coloring Pages" },
];

const inputCls =
  "h-12 w-full rounded-2xl border border-ink/15 bg-white px-4 text-[15px] text-ink focus:border-ink/40 focus:outline-none";
const labelCls = "mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/60";

export const FreebieForm = ({ item, onDone, onCancel }) => {
  const isEdit = Boolean(item);
  const [title, setTitle] = useState(item?.title || "");
  const [category, setCategory] = useState(item?.category || "stationery");
  const [sortOrder, setSortOrder] = useState(item?.sort_order ?? 0);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  const submit = async (status) => {
    setError("");
    if (!title.trim()) {
      setError("Please give the freebie a title.");
      return;
    }
    if (!isEdit && !preview) {
      setError("Please choose a preview image.");
      return;
    }
    if (!isEdit && !file) {
      setError("Please choose the downloadable file.");
      return;
    }
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("category", category);
    fd.append("sort_order", String(sortOrder));
    fd.append("status", status);
    if (preview) fd.append("preview", preview);
    if (file) fd.append("file", file);

    setSaving(true);
    setProgress(0);
    try {
      if (isEdit) {
        await api.put(`/admin/freebies/${item.id}`, fd, {
          onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
        });
        toast.success("Freebie updated — hearts and downloads kept");
      } else {
        await api.post("/admin/freebies", fd, {
          onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
        });
        toast.success(status === "published" ? "Freebie published" : "Draft saved");
      }
      onDone();
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="freebie-form">
      <div className="mx-auto w-full max-w-xl px-4 py-6 sm:px-6 sm:py-10">
        <button
          type="button"
          data-testid="form-back-button"
          onClick={onCancel}
          className="flex min-h-[44px] items-center gap-2 text-[13px] font-semibold text-ink/60 transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Freebies
        </button>
        <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink">
          {isEdit ? "Edit Freebie" : "Add New Freebie"}
        </h1>

        <form onSubmit={(e) => e.preventDefault()} className="mt-8 space-y-5">
          <div>
            <label htmlFor="fb-title" className={labelCls}>Title</label>
            <input
              id="fb-title"
              data-testid="form-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Dachshund Floral Note Cards"
              className={inputCls}
            />
          </div>

          <div>
            <label htmlFor="fb-category" className={labelCls}>Category</label>
            <select
              id="fb-category"
              data-testid="form-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={inputCls}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="fb-sort" className={labelCls}>Sort order <span className="font-normal normal-case tracking-normal text-ink/40">(lower shows first)</span></label>
            <input
              id="fb-sort"
              data-testid="form-sort-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              className={inputCls}
            />
          </div>

          <div>
            <label htmlFor="fb-preview" className={labelCls}>Preview image</label>
            {isEdit && !preview && (
              <img src={fileUrl(item.preview_url)} alt="" className="mb-2 h-20 w-36 rounded-xl object-cover" data-testid="form-current-preview" />
            )}
            <label
              htmlFor="fb-preview"
              className="flex min-h-[56px] w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/25 bg-white/70 px-4 text-sm font-medium text-ink/70 transition-colors hover:border-ink/50"
            >
              <Upload className="h-4 w-4" />
              {preview ? preview.name : isEdit ? "Replace preview image" : "Choose preview image"}
            </label>
            <input
              id="fb-preview"
              data-testid="form-preview-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="sr-only"
              onChange={(e) => setPreview(e.target.files?.[0] || null)}
            />
          </div>

          <div>
            <label htmlFor="fb-file" className={labelCls}>Download file</label>
            {isEdit && !file && (
              <p className="mb-2 text-xs text-ink/55" data-testid="form-current-file">Current: {item.file_name}</p>
            )}
            <label
              htmlFor="fb-file"
              className="flex min-h-[56px] w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/25 bg-white/70 px-4 text-sm font-medium text-ink/70 transition-colors hover:border-ink/50"
            >
              <Upload className="h-4 w-4" />
              {file ? file.name : isEdit ? "Replace download file" : "Choose download file (PDF, PNG, JPG, ZIP…)"}
            </label>
            <input
              id="fb-file"
              data-testid="form-file-input"
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.zip,.txt"
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          {saving && (
            <div data-testid="upload-progress" className="space-y-1.5">
              <div className="h-2 w-full overflow-hidden rounded-full bg-ink/10">
                <div className="h-full rounded-full bg-[#6E8CA3] transition-[width]" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-center text-xs text-ink/55">Uploading… {progress}%</p>
            </div>
          )}

          {error && (
            <p role="alert" data-testid="form-error" className="text-[13px] font-medium text-[#A03D2A]">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              data-testid="save-draft-button"
              onClick={() => submit("draft")}
              disabled={saving}
              className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full border border-ink/20 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink/75 transition-colors hover:border-ink/50 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Draft
            </button>
            <button
              type="button"
              data-testid="publish-button"
              onClick={() => submit("published")}
              disabled={saving}
              className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-[#6E8CA3] text-[13px] font-semibold uppercase tracking-[0.14em] text-ivory transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save & Publish" : "Publish Freebie"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
